import { FetchError } from 'ofetch'
import type { User } from '~/types'

export const useUserStore = defineStore('user', () => {
  const user = ref<Maybe<User.Model>>(null)
  const { search } = useUserApi()
  const { clear: clearCSRFToken } = useCSRFToken()
  const isLoading = ref<boolean>(false)

  const { handleException } = useHandleException()

  // Getters
  const isLoggedIn = computed<boolean>(() => hasLoginCookie.value && !!user.value?.id)
  const firstName = computed<Maybe<string>>(() => user.value?.firstName || null)
  const lastName = computed<Maybe<string>>(() => user.value?.lastName || null)
  const email = computed<Maybe<string>>(() => user.value?.email || null)
  const fullName = computed<Maybe<string>>(() => lastName.value && firstName.value ? `${firstName.value} ${lastName.value}` : null)

  const bypassCookieName = useBypassCookieName()
  const bypassCookie = useCookie(bypassCookieName, { readonly: true })

  const hasLoginCookie = computed<boolean>(() => bypassCookie.value !== undefined)

  // Actions
  const fetchData = async (): Promise<User.Model | undefined> => {
    clear()
    isLoading.value = true
    try {
      const { data } = await search()

      return data
    }
    catch (ex: unknown) {
      if (ex instanceof FetchError && ex.statusCode === 401) {
        return
      }
      handleException(ex)
    }
    finally {
      isLoading.value = false
    }
  }

  const clear = (): void => {
    user.value = null
    clearCSRFToken()
  }

  const set = (payload: MaybeRef<Maybe<User.Model>>): void => {
    user.value = unref(payload)
  }

  return {
    email,
    hasLoginCookie,
    firstName,
    fullName,
    isLoggedIn,
    lastName,
    user,

    clear,
    fetchData,
    set,
  }
})
